// The shared heading block for every publisher-admin page (CTV-759).
//
// Owns title, subtitle, actions, and the Station scope selector. The header
// is where the selector's MEANING comes from (ADR-0036): a control among
// navigation links reads as navigation, whereas the same control beside a
// page title reads as "here is what this page is showing". Do not move it
// back into the sidebar.
//
// Retrofitting all thirteen pages onto this also settles heading markup that
// previously spanned max-w-3xl through max-w-6xl across three structural
// shapes.

import React from 'react';
import StationScopeSelector from './StationScopeSelector';

export interface AdminPageHeaderProps {
  /** ReactNode, not string: the promotion detail page's title is a `<code>`. */
  title: React.ReactNode;
  /** Small uppercase line above the title. Used by the record detail pages. */
  eyebrow?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Page-level buttons (Refresh, Export CSV, …) rendered beside the selector. */
  actions?: React.ReactNode;
  /**
   * 'flexible' — this page can render All stations, so the selector offers it.
   * 'bound'    — this page writes configuration to one station, so the
   *              selector offers the stations but not All.
   * 'none'     — no selector at all. For RECORD DETAIL pages: a submission,
   *              promotion or salesperson belongs to one station, so there is
   *              no other station to switch to and the control would only
   *              offer to navigate away from what you are looking at.
   */
  scopeMode: 'flexible' | 'bound' | 'none';
  /** The scope in effect: a station slug, or `all`. Unused when 'none'. */
  currentSlug: string;
}

const AdminPageHeader: React.FC<AdminPageHeaderProps> = ({
  title,
  eyebrow,
  subtitle,
  actions,
  scopeMode,
  currentSlug,
}) => (
  <div className="mb-6">
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div className="min-w-0">
        {eyebrow && (
          <div className="text-xs uppercase tracking-wide text-slate-500 mb-1">{eyebrow}</div>
        )}
        <h1 className="text-2xl font-semibold text-slate-900">{title}</h1>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {actions}
        {scopeMode !== 'none' && (
          <StationScopeSelector scopeMode={scopeMode} currentSlug={currentSlug} />
        )}
      </div>
    </div>
    {subtitle && <p className="mt-1 text-sm text-slate-600">{subtitle}</p>}
  </div>
);

export default AdminPageHeader;
